import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useDismissMultipleNotificationsMutation,
  useDismissNotificationMutation,
  useMarkAllNotificationsAsReadMutation,
  useMarkMultipleNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
  useSetDefaultNotificationPreferencesMutation,
  useUpdateMultipleNotificationPreferencesMutation,
  useUpdateNotificationPreferenceMutation,
} from '@/hooks/mutations/useNotificationsMutations'
import { useNotificationReaderRealtime } from '@/hooks/queries/useNotificationReaderRealtime'
import { useNotificationReadSession } from '@/hooks/queries/useNotificationReadSession'
import {
  useNotificationPreferencesQuery,
  useNotificationsQuery,
  useUnreadNotificationCountQuery,
} from '@/hooks/queries/useNotificationsQuery'
import { queryKeys } from '@/hooks/query-keys'
import { browserNotificationService } from '@/services/browserNotificationService'
import { useOrganizationStore } from '@/store/organization.store'
import { hashKey } from '@tanstack/react-query'

import type { Notification } from '@scrambl/shared'
import {
  NotificationChannel,
  NotificationQueryDto,
  NotificationType,
} from '@scrambl/shared'

const DEFAULT_FILTERS = { page: 1, limit: 20 }
const EMPTY_NOTIFICATIONS: Notification[] = []
const UNREAD_COUNT_PREFIX = queryKeys.notifications.unreadCount()

// Pagination keeps only filters in local state; bodies belong to owned readers.
export const useNotifications = (autoFetch = true) => {
  const session = useNotificationReadSession()
  const identity = hashKey([session.key])
  const [selection, setSelection] = useState<{
    identity: string
    filters: NotificationQueryDto & { page: number; limit: number }
  }>({ identity, filters: DEFAULT_FILTERS })
  const filters =
    selection.identity === identity ? selection.filters : DEFAULT_FILTERS
  const listPrefix = useMemo(
    () => queryKeys.notifications.list(filters),
    [filters],
  )
  const query = useNotificationsQuery(filters, { enabled: autoFetch })
  const unread = useUnreadNotificationCountQuery({ enabled: autoFetch })
  useNotificationReaderRealtime(listPrefix, session, autoFetch)
  useNotificationReaderRealtime(UNREAD_COUNT_PREFIX, session, autoFetch)
  const page = query.data?.page ?? filters.page
  const hasMore = !!query.data && page < query.data.totalPages
  const hasPrevious = page > 1

  // Stable across pages: applying the same filter cannot reset a later page or
  // create a filter-effect render loop. A real filter change starts at page 1.
  const fetchNotifications = useCallback(
    async (patch?: NotificationQueryDto) => {
      session.assertCurrent()
      setSelection(previous => {
        const current =
          previous.identity === identity ? previous.filters : DEFAULT_FILTERS
        const next = { ...patch, ...DEFAULT_FILTERS }
        if (
          previous.identity === identity &&
          hashKey([{ ...current, page: 1 }]) === hashKey([next])
        )
          return previous
        return { identity, filters: next }
      })
    },
    [session, identity],
  )
  const goToPage = useCallback(
    async (next: number) => {
      session.assertCurrent()
      if (
        query.isFetching ||
        next < 1 ||
        (next > page && (!query.data || next > query.data.totalPages))
      )
        return
      // Same-frame duplicate clicks choose one target instead of skipping a page.
      setSelection({ identity, filters: { ...filters, page: next } })
    },
    [session, identity, filters, page, query.data, query.isFetching],
  )
  const fetchMoreNotifications = useCallback(
    () => goToPage(page + 1),
    [goToPage, page],
  )
  const fetchPreviousNotifications = useCallback(
    () => goToPage(page - 1),
    [goToPage, page],
  )
  const retry = useCallback(async () => {
    session.assertCurrent()
    await query.refetch()
  }, [session, query.refetch])
  const fetchUnreadCount = useCallback(async () => {
    session.assertCurrent()
    await unread.refetch()
  }, [session, unread.refetch])
  const markAsReadMutation = useMarkNotificationAsReadMutation()
  const markMultipleAsReadMutation =
    useMarkMultipleNotificationsAsReadMutation()
  const markAllAsReadMutation = useMarkAllNotificationsAsReadMutation()
  const dismissNotificationMutation = useDismissNotificationMutation()
  const dismissMultipleMutation = useDismissMultipleNotificationsMutation()
  return {
    notifications: query.data?.data ?? EMPTY_NOTIFICATIONS,
    unreadCount: unread.data?.count ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    readerKey: hashKey([...listPrefix, session.key]),
    page,
    hasMore,
    hasPrevious,
    fetchNotifications,
    fetchMoreNotifications,
    fetchPreviousNotifications,
    retry,
    fetchUnreadCount,
    markAsRead: markAsReadMutation.mutateAsync,
    markMultipleAsRead: markMultipleAsReadMutation.mutateAsync,
    markAllAsRead: markAllAsReadMutation.mutateAsync,
    dismissNotification: dismissNotificationMutation.mutateAsync,
    dismissMultiple: dismissMultipleMutation.mutateAsync,
  }
}

// Hook for notification preferences
export const useNotificationPreferences = () => {
  const { currentOrganization } = useOrganizationStore()
  const { data: preferences = [] } = useNotificationPreferencesQuery({
    enabled: !!currentOrganization?.id,
  })

  const {
    updatePreference: updatePreferenceMutation,
    updateMultiplePreferences: updateMultiplePreferencesMutation,
    setDefaultPreferences: setDefaultPreferencesMutation,
  } = useNotificationPreferencesMutations()

  const isPreferenceEnabled = (
    channel: NotificationChannel,
    type: NotificationType,
  ) => {
    const preference = preferences.find(
      p => p.channel === channel && p.type === type,
    )
    return preference?.enabled ?? true // Default to enabled if no preference
  }

  const togglePreference = async (
    channel: NotificationChannel,
    type: NotificationType,
  ) => {
    const currentEnabled = isPreferenceEnabled(channel, type)
    await updatePreferenceMutation.mutateAsync({
      channel,
      type,
      enabled: !currentEnabled,
    })
  }

  return {
    preferences,
    isPreferenceEnabled,
    togglePreference,
    updatePreference: updatePreferenceMutation.mutateAsync,
    updateMultiplePreferences: updateMultiplePreferencesMutation.mutateAsync,
    setDefaultPreferences: setDefaultPreferencesMutation.mutateAsync,
  }
}

// Only the owned count reader drives the OS badge.
export const useUnreadNotificationCount = () => {
  const session = useNotificationReadSession()
  const { data } = useUnreadNotificationCountQuery()
  const count = data?.count ?? 0
  useNotificationReaderRealtime(UNREAD_COUNT_PREFIX, session)
  useEffect(() => {
    const current = () => {
      try {
        session.assertUnchanged()
        return true
      } catch {
        return false
      }
    }
    // Disabled/signed-out readers clear the badge; a later session cancels the
    // pending import through effect cleanup, including zero-count writes.
    let active = true
    void browserNotificationService.setBadgeCount(
      count,
      () => active && current(),
    )
    return () => {
      active = false
    }
  }, [count, session])
  return count
}

// Hook for filtering notifications
export const useFilteredNotifications = (filters: NotificationQueryDto) => {
  const { data: notificationsData, isLoading } = useNotificationsQuery(filters)

  return {
    notifications: notificationsData?.data || [],
    isLoading,
  }
}

// Hook for notification actions
export const useNotificationActions = () => {
  const markAsReadMutation = useMarkNotificationAsReadMutation()
  const markMultipleAsReadMutation =
    useMarkMultipleNotificationsAsReadMutation()
  const markAllAsReadMutation = useMarkAllNotificationsAsReadMutation()
  const dismissNotificationMutation = useDismissNotificationMutation()
  const dismissMultipleMutation = useDismissMultipleNotificationsMutation()

  return {
    markNotificationAsRead: markAsReadMutation.mutateAsync,
    markAllNotificationsAsRead: markAllAsReadMutation.mutateAsync,
    dismissNotificationById: dismissNotificationMutation.mutateAsync,
    markMultipleAsRead: markMultipleAsReadMutation.mutateAsync,
    dismissMultiple: dismissMultipleMutation.mutateAsync,
  }
}

// Helper hook for notification preferences mutations
function useNotificationPreferencesMutations() {
  return {
    updatePreference: useUpdateNotificationPreferenceMutation(),
    updateMultiplePreferences:
      useUpdateMultipleNotificationPreferencesMutation(),
    setDefaultPreferences: useSetDefaultNotificationPreferencesMutation(),
  }
}
